import { useCallback, useEffect, useRef, useState } from 'react';
import { Info, Loader2, MessageCircle, Plus, QrCode, RefreshCw, Trash2 } from 'lucide-react';

import { Button as BaseButton } from '../../components_dashboard/ui/button';
import { ToggleSwitch } from '../../components/ui/toggle-switch';
import { cn } from '../../lib/utils';
import { canAccessChatbot, canAccessCrm, canAccessWorkflows } from '../../lib/userAccess';
import { Input } from '../../components_dashboard/ui/input';
import { Label } from '../../components_dashboard/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components_dashboard/ui/dialog';
import { toast } from '../../components/CustomToast';
import {
  QR_POLL_INTERVAL_MS,
  QR_POLL_TIMEOUT_MS,
  whatsappApi,
} from './whatsapp-api';

// Use the Settings palette without changing buttons elsewhere in the app.
function Button({ variant, className, ...props }) {
  return (
    <BaseButton
      {...props}
      variant={variant}
      className={cn(
        'rounded-lg font-semibold focus-visible:ring-brand',
        variant === 'outline'
          ? 'border-line bg-elevated text-ink hover:bg-soft hover:text-ink'
          : 'bg-brand text-white shadow-none hover:bg-brand-hover',
        className
      )}
    />
  );
}

const STATUS_STYLES = {
  connected: 'bg-success-soft text-success border-success/20',
  qr_pending: 'bg-warning-soft text-warning border-warning/20',
  qr_expired: 'bg-warning-soft text-warning border-warning/20',
  disconnected: 'bg-danger-soft text-danger border-danger/20',
  failed: 'bg-danger-soft text-danger border-danger/20',
};

const STATUS_LABELS = {
  connected: 'Connected',
  pending: 'Pending',
  qr_pending: 'Waiting for scan',
  qr_expired: 'QR expired',
  disconnected: 'Disconnected',
  failed: 'Failed',
};

const INTEGRATION_LABELS = {
  'WHATSAPP-BAILEYS': 'QR code',
  'WHATSAPP-BUSINESS': 'Meta Business API',
};

const PURPOSE_OPTIONS = [
  { value: 'chatbot', label: 'WhatsApp Chatbot' },
  { value: 'workflow', label: 'Workflow follow-ups' },
  { value: 'crm', label: 'CRM chat' },
];

function StatusBadge({ status }) {
  const style = STATUS_STYLES[status] || 'bg-soft text-ink-2 border-line';
  return (
    <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${style}`}>
      {STATUS_LABELS[status] || status || 'Unknown'}
    </span>
  );
}

function errorMessage(error, fallback) {
  return error?.response?.data?.detail || error?.message || fallback;
}

export default function WhatsAppInstancesPanel({ token, clientId, user }) {
  // A purpose toggle is pointless (and misleading) to turn on for a feature
  // this account can't otherwise reach -- gate each one by the same admin
  // Feature Flags used to show/hide that feature's own tab.
  const chatbotAllowed = canAccessChatbot(user);
  const workflowAllowed = canAccessWorkflows(user);
  const crmAllowed = canAccessCrm(user);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyInstance, setBusyInstance] = useState('');
  const [chooserOpen, setChooserOpen] = useState(false);
  const [metaOpen, setMetaOpen] = useState(false);
  const [metaForm, setMetaForm] = useState({ phone_number_id: '', business_id: '', app_id: '', access_token: '' });
  const [metaSaving, setMetaSaving] = useState(false);
  const [pairing, setPairing] = useState(null);
  const [webhooks, setWebhooks] = useState({});
  const [webhookOpen, setWebhookOpen] = useState(false);
  const [webhookForm, setWebhookForm] = useState({ instance_name: '', app_secret: '' });
  const [webhookSaving, setWebhookSaving] = useState(false);
  // The fresh verify token, shown once after creation/rotation and never
  // readable again -- kept out of the webhooks map on purpose.
  const [createdToken, setCreatedToken] = useState(null);

  // The poll runs on a timer that must not outlive the dialog, and the dialog
  // can close mid-request. Both are tracked here so a late response cannot
  // resurrect a closed dialog.
  const pollTimer = useRef(null);
  const pollDeadline = useRef(0);
  const pairingOpen = useRef(false);

  const load = useCallback(async () => {
    if (!token || !clientId) return;
    setLoading(true);
    try {
      const [instanceList, credentialList] = await Promise.all([
        whatsappApi.listForClient(token, clientId),
        whatsappApi.listWebhookCredentials(token).catch(() => []),
      ]);
      setInstances(instanceList);
      setWebhooks(Object.fromEntries(credentialList.map((entry) => [entry.instance_name, entry])));
    } catch (error) {
      toast.error(errorMessage(error, 'Could not load WhatsApp instances.'));
    } finally {
      setLoading(false);
    }
  }, [token, clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const stopPolling = useCallback(() => {
    pairingOpen.current = false;
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const pollQr = useCallback(
    async (instanceName) => {
      if (!pairingOpen.current) return;

      if (Date.now() > pollDeadline.current) {
        // Give up rather than keep a dead code on screen. The instance stays,
        // so "Show QR" resumes pairing without creating a second one.
        setPairing((current) =>
          current ? { ...current, status: 'qr_expired', qrCode: null } : current
        );
        stopPolling();
        return;
      }

      try {
        const result = await whatsappApi.refreshQr(token, instanceName);
        if (!pairingOpen.current) return;

        if (result.status === 'connected') {
          setPairing((current) => (current ? { ...current, status: 'connected', qrCode: null } : current));
          stopPolling();
          toast.success('WhatsApp connected.');
          load();
          return;
        }

        setPairing((current) =>
          current ? { ...current, status: result.status, qrCode: result.qr_code || current.qrCode } : current
        );
      } catch (error) {
        if (!pairingOpen.current) return;
        toast.error(errorMessage(error, 'Could not refresh the QR code.'));
      }

      if (pairingOpen.current) {
        pollTimer.current = setTimeout(() => pollQr(instanceName), QR_POLL_INTERVAL_MS);
      }
    },
    [token, load, stopPolling]
  );

  const beginPairing = useCallback(
    (instanceName, qrCode, status) => {
      setPairing({ instanceName, qrCode, status: status || 'qr_pending' });
      pairingOpen.current = true;
      pollDeadline.current = Date.now() + QR_POLL_TIMEOUT_MS;
      pollTimer.current = setTimeout(() => pollQr(instanceName), QR_POLL_INTERVAL_MS);
    },
    [pollQr]
  );

  const handleCreateQr = useCallback(async () => {
    setChooserOpen(false);
    setBusyInstance('new');
    try {
      const result = await whatsappApi.createQrInstance(token, clientId);
      await load();
      beginPairing(result.instance_name, result.qr_code, result.status);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not create the WhatsApp instance.'));
    } finally {
      setBusyInstance('');
    }
  }, [token, clientId, load, beginPairing]);

  const handleCreateMeta = useCallback(async () => {
    const phoneNumberId = metaForm.phone_number_id.trim();
    const businessId = metaForm.business_id.trim();
    const appId = metaForm.app_id.trim();
    const accessToken = metaForm.access_token.trim();
    if (!phoneNumberId || !businessId || !appId || !accessToken) {
      toast.error('Phone number ID, business ID, App ID and access token are all required.');
      return;
    }

    setMetaSaving(true);
    try {
      await whatsappApi.createBusinessInstance(token, {
        client_id: clientId,
        phone_number_id: phoneNumberId,
        business_id: businessId,
        app_id: appId,
        access_token: accessToken,
      });
      // The token is not stored anywhere we can read it back, so clear the form
      // rather than leave a live credential sitting in component state.
      setMetaForm({ phone_number_id: '', business_id: '', app_id: '', access_token: '' });
      setMetaOpen(false);
      toast.success('WhatsApp Business instance created.');
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not create the Meta instance.'));
    } finally {
      setMetaSaving(false);
    }
  }, [token, clientId, metaForm, load]);

  const handleRefreshState = useCallback(
    async (instanceName) => {
      setBusyInstance(instanceName);
      try {
        const state = await whatsappApi.readState(token, instanceName);
        setInstances((current) =>
          current.map((entry) =>
            entry.instance_name === instanceName ? { ...entry, status: state.status } : entry
          )
        );
      } catch (error) {
        toast.error(errorMessage(error, 'Could not read the connection state.'));
      } finally {
        setBusyInstance('');
      }
    },
    [token]
  );

  const handleShowQr = useCallback(
    async (instanceName) => {
      setBusyInstance(instanceName);
      try {
        const result = await whatsappApi.refreshQr(token, instanceName);
        if (result.status === 'connected') {
          toast.success('This instance is already connected.');
          load();
          return;
        }
        beginPairing(instanceName, result.qr_code, result.status);
      } catch (error) {
        toast.error(errorMessage(error, 'Could not fetch a QR code.'));
      } finally {
        setBusyInstance('');
      }
    },
    [token, load, beginPairing]
  );

  const handleUseInstance = useCallback(
    async (instanceName) => {
      setBusyInstance(instanceName);
      try {
        await whatsappApi.assignToClient(token, instanceName, clientId);
        toast.success('New messages will send from this instance.');
        load();
      } catch (error) {
        toast.error(errorMessage(error, 'Could not switch instance.'));
      } finally {
        setBusyInstance('');
      }
    },
    [token, clientId, load]
  );

  const handleTogglePurpose = useCallback(
    async (instance, purpose, checked) => {
      const nextPurposes = checked
        ? [...instance.purposes, purpose]
        : instance.purposes.filter((entry) => entry !== purpose);

      setBusyInstance(instance.instance_name);
      // Optimistic: also drop the purpose from whichever sibling instance
      // currently holds it, mirroring what the server is about to do, so the
      // list doesn't flash a stale "double-bound" state for a moment.
      setInstances((current) =>
        current.map((entry) => {
          if (entry.instance_name === instance.instance_name) {
            return { ...entry, purposes: nextPurposes };
          }
          if (checked && entry.purposes.includes(purpose)) {
            return { ...entry, purposes: entry.purposes.filter((p) => p !== purpose) };
          }
          return entry;
        })
      );
      try {
        await whatsappApi.setPurposes(token, instance.instance_name, clientId, nextPurposes);
      } catch (error) {
        toast.error(errorMessage(error, 'Could not update this instance.'));
        load();
      } finally {
        setBusyInstance('');
      }
    },
    [token, clientId, load]
  );

  const [deleteTarget, setDeleteTarget] = useState('');
  const [deleting, setDeleting] = useState(false);

  const openWebhookDialog = useCallback((instanceName) => {
    setWebhookForm({ instance_name: instanceName, app_secret: '' });
    setCreatedToken(null);
    setWebhookOpen(true);
  }, []);

  const handleSaveWebhook = useCallback(async () => {
    const instanceName = webhookForm.instance_name;
    const appSecret = webhookForm.app_secret.trim();
    if (!instanceName || appSecret.length < 8) {
      toast.error('Paste the app secret from the Meta app dashboard (min 8 characters).');
      return;
    }
    setWebhookSaving(true);
    try {
      const created = await whatsappApi.createWebhookCredential(token, {
        instance_name: instanceName,
        app_secret: appSecret,
      });
      setCreatedToken(created.verify_token);
      setWebhookForm({ instance_name: instanceName, app_secret: '' });
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not save webhook credentials.'));
    } finally {
      setWebhookSaving(false);
    }
  }, [token, webhookForm, load]);

  const confirmDelete = useCallback(async () => {
    const instanceName = deleteTarget;
    if (!instanceName) return;
    setDeleting(true);
    setBusyInstance(instanceName);
    try {
      await whatsappApi.deleteInstance(token, instanceName);
      toast.success('Instance deleted.');
      setDeleteTarget('');
      load();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not delete the instance.'));
    } finally {
      setDeleting(false);
      setBusyInstance('');
    }
  }, [token, load, deleteTarget]);

  const closePairing = useCallback(() => {
    stopPolling();
    setPairing(null);
    load();
  }, [stopPolling, load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-ink">WhatsApp Connections</h2>
          <p className="mt-1 text-sm text-ink-3">
            Connect a number for AI chats, CRM conversations, and workflow follow-ups.
          </p>
        </div>
        <Button onClick={() => setChooserOpen(true)} disabled={!clientId || busyInstance === 'new'} className="shrink-0">
          {busyInstance === 'new' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Add WhatsApp
        </Button>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-brand/20 bg-brand-soft px-4 py-3 text-sm text-ink-2">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
        <p>
          Your default connection sends new messages. You can also choose a separate connection for each feature.
          Turn on WhatsApp Chatbot below to attach an AI agent and start replying.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 px-4 py-6 text-sm rounded-xl border border-line bg-soft text-ink-3">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading instances...
        </div>
      ) : instances.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-line bg-elevated px-6 py-12 text-center shadow-sm">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-soft text-brand">
            <MessageCircle className="h-6 w-6" />
          </div>
          <h3 className="text-base font-semibold text-ink">Connect your first WhatsApp number</h3>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-ink-3">
            Scan a QR code with your phone or connect through the Meta Business API to get started.
          </p>
          <Button className="mt-5" onClick={() => setChooserOpen(true)} disabled={!clientId || busyInstance === 'new'}>
            {busyInstance === 'new' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Connect WhatsApp
          </Button>
          <p className="mt-3 text-xs text-ink-3">Supports WhatsApp and WhatsApp Business</p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-ink-3">Changes apply to new messages. Scheduled messages keep their original connection.</p>
          {instances.map((instance) => (
            <div
              key={instance.instance_name}
              className="rounded-xl border border-line bg-elevated p-5 shadow-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className="font-medium truncate text-ink">
                      {instance.instance_name}
                    </span>
                    <StatusBadge status={instance.status} />
                    {instance.is_current_for_client && (
                      <span className="rounded-full border border-brand/20 bg-brand-soft px-2.5 py-0.5 text-xs font-medium text-brand">
                        Default
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-ink-3">
                    {INTEGRATION_LABELS[instance.integration] || instance.integration}
                    {instance.phone_number ? ` • ${instance.phone_number}` : ''}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRefreshState(instance.instance_name)}
                    disabled={busyInstance === instance.instance_name}
                  >
                    <RefreshCw className="w-4 h-4 mr-1.5" />
                    Check
                  </Button>
                  {instance.integration === 'WHATSAPP-BAILEYS' && instance.status !== 'connected' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleShowQr(instance.instance_name)}
                      disabled={busyInstance === instance.instance_name}
                    >
                      <QrCode className="w-4 h-4 mr-1.5" />
                      Show QR
                    </Button>
                  )}
                  {!instance.is_current_for_client && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUseInstance(instance.instance_name)}
                      disabled={busyInstance === instance.instance_name}
                    >
                      Set as default
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label={`Delete ${instance.instance_name}`}
                    onClick={() => setDeleteTarget(instance.instance_name)}
                    disabled={busyInstance === instance.instance_name}
                  >
                    <Trash2 className="w-4 h-4 text-danger" />
                  </Button>
                </div>
              </div>

              {(chatbotAllowed || workflowAllowed || crmAllowed) && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-line pt-3 mt-3">
                  <span className="text-xs font-medium text-ink-3">Used for:</span>
                  {PURPOSE_OPTIONS.filter((option) =>
                    option.value === 'chatbot' ? chatbotAllowed
                      : option.value === 'workflow' ? workflowAllowed
                        : crmAllowed
                  ).map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center gap-1.5 text-sm text-ink-2 cursor-pointer"
                    >
                      <ToggleSwitch
                        ariaLabel={option.label}
                        checked={instance.purposes.includes(option.value)}
                        disabled={busyInstance === instance.instance_name}
                        onChange={(checked) =>
                          handleTogglePurpose(instance, option.value, checked)
                        }
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              )}
              {instance.integration === 'WHATSAPP-BUSINESS' && (
                <div className="border-t border-line pt-3 mt-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-medium text-ink-3">
                      Meta webhook {webhooks[instance.instance_name] ? '• connected' : '• not set up'}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openWebhookDialog(instance.instance_name)}
                    >
                      {webhooks[instance.instance_name] ? 'Rotate token' : 'Set up webhook'}
                    </Button>
                  </div>
                  <p className="mt-1 text-xs text-ink-3">
                    Stores a verify token and app secret for this client's Meta app.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={chooserOpen} onOpenChange={setChooserOpen}>
        <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] overflow-y-auto rounded-2xl border-line bg-elevated p-6 text-ink shadow-xl sm:rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-ink">Add WhatsApp</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-ink-3">
              Choose how your WhatsApp number connects.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <button
              type="button"
              onClick={handleCreateQr}
              className="w-full rounded-xl border border-line bg-elevated p-4 text-left transition-colors hover:border-brand/40 hover:bg-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <div className="flex items-center gap-2 font-semibold text-ink"><QrCode className="h-4 w-4 text-brand" /> Scan a QR code</div>
              <p className="mt-1 text-sm text-ink-3">
                Links an ordinary WhatsApp or WhatsApp Business app. Someone with your phone
                has to scan a code within about three minutes.
              </p>
            </button>

            <button
              type="button"
              onClick={() => {
                setChooserOpen(false);
                setMetaOpen(true);
              }}
              className="w-full rounded-xl border border-line bg-elevated p-4 text-left transition-colors hover:border-brand/40 hover:bg-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <div className="flex items-center gap-2 font-semibold text-ink"><MessageCircle className="h-4 w-4 text-brand" /> Meta Business API</div>
              <p className="mt-1 text-sm text-ink-3">
                The official Cloud API. Needs the phone number ID, business account ID and a
                permanent access token from Meta. No phone required.
              </p>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={metaOpen}
        onOpenChange={(open) => {
          setMetaOpen(open);
          if (!open) setMetaForm({ phone_number_id: '', business_id: '', app_id: '', access_token: '' });
        }}
      >
        <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] overflow-y-auto rounded-2xl border-line bg-elevated p-6 text-ink shadow-xl sm:rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-ink">Meta Business API</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-ink-3">
              Enter your credentials from WhatsApp Manager to connect your business number.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="wa-phone-number-id">Phone number ID</Label>
              <Input
                className="rounded-lg border-line bg-elevated text-ink placeholder:text-ink-3 focus-visible:ring-brand"
                id="wa-phone-number-id"
                value={metaForm.phone_number_id}
                onChange={(event) =>
                  setMetaForm((form) => ({ ...form, phone_number_id: event.target.value }))
                }
                placeholder="109876543210987"
              />
              <p className="text-xs text-ink-3">
                The numeric ID Meta shows next to the number — not the phone number itself.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="wa-business-id">WhatsApp business account ID</Label>
              <Input
                className="rounded-lg border-line bg-elevated text-ink placeholder:text-ink-3 focus-visible:ring-brand"
                id="wa-business-id"
                value={metaForm.business_id}
                onChange={(event) =>
                  setMetaForm((form) => ({ ...form, business_id: event.target.value }))
                }
                placeholder="123456789012345"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="wa-app-id">Meta App ID</Label>
              <Input
                className="rounded-lg border-line bg-elevated text-ink placeholder:text-ink-3 focus-visible:ring-brand"
                id="wa-app-id"
                value={metaForm.app_id}
                onChange={(event) =>
                  setMetaForm((form) => ({ ...form, app_id: event.target.value }))
                }
                placeholder="2127223287829381"
              />
              <p className="text-xs text-ink-3">
                The app that owns the WABA — upload sessions start on it.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="wa-access-token">Permanent access token</Label>
              <Input
                className="rounded-lg border-line bg-elevated text-ink placeholder:text-ink-3 focus-visible:ring-brand"
                id="wa-access-token"
                type="password"
                autoComplete="off"
                value={metaForm.access_token}
                onChange={(event) =>
                  setMetaForm((form) => ({ ...form, access_token: event.target.value }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMetaOpen(false)} disabled={metaSaving}>
              Cancel
            </Button>
            <Button onClick={handleCreateMeta} disabled={metaSaving}>
              {metaSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create instance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={webhookOpen}
        onOpenChange={(open) => {
          setWebhookOpen(open);
          if (!open) {
            setWebhookForm({ instance_name: '', app_secret: '' });
            setCreatedToken(null);
          }
        }}
      >
        <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl border-line bg-elevated p-6 text-ink shadow-xl sm:rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-ink">Meta webhook</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-ink-3">
              {webhookForm.instance_name} receives Meta events through one shared callback URL.
              Paste the app secret from the client's Meta app, then copy the verify token into Meta.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {createdToken ? (
              <div className="space-y-2">
                <Label>Verify token — copy now, it is never shown again</Label>
                <p className="break-all rounded-lg border border-brand/30 bg-brand-soft px-3 py-2 font-mono text-xs text-ink">
                  {createdToken}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="wa-app-secret">App secret</Label>
                <Input
                  className="rounded-lg border-line bg-elevated text-ink placeholder:text-ink-3 focus-visible:ring-brand"
                  id="wa-app-secret"
                  type="password"
                  autoComplete="off"
                  value={webhookForm.app_secret}
                  onChange={(event) =>
                    setWebhookForm((form) => ({ ...form, app_secret: event.target.value }))
                  }
                  placeholder="From the Meta app dashboard"
                />
                <p className="text-xs text-ink-3">
                  Saving generates a new verify token and replaces any previous one.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setWebhookOpen(false)}
              disabled={webhookSaving}
            >
              {createdToken ? 'Done' : 'Cancel'}
            </Button>
            {!createdToken && (
              <Button onClick={handleSaveWebhook} disabled={webhookSaving}>
                {webhookSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Generate token
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(pairing)} onOpenChange={(open) => (open ? null : closePairing())}>
        <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] overflow-y-auto rounded-2xl border-line bg-elevated p-6 text-ink shadow-xl sm:rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-ink">Scan to connect</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-ink-3">
              On your phone: WhatsApp → Settings → Linked devices → Link a device.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center gap-3 py-2">
            {pairing?.status === 'connected' ? (
              <p className="py-8 text-sm font-medium text-success">
                Connected. Your account now sends from {pairing.instanceName}.
              </p>
            ) : pairing?.status === 'qr_expired' ? (
              <div className="py-8 text-center">
                <p className="text-sm text-ink-2">
                  The code expired before it was scanned.
                </p>
                <Button
                  className="mt-3"
                  variant="outline"
                  onClick={() => handleShowQr(pairing.instanceName)}
                >
                  Get a new code
                </Button>
              </div>
            ) : pairing?.qrCode ? (
              <>
                <img
                  src={pairing.qrCode}
                  alt="WhatsApp pairing QR code"
                  className="h-56 w-56 rounded-xl border border-line bg-white p-2"
                />
                <p className="text-xs text-ink-3">
                  The code refreshes on its own. This dialog stops trying after three minutes.
                </p>
              </>
            ) : (
              <div className="flex items-center gap-2 py-8 text-sm text-ink-3">
                <Loader2 className="w-4 h-4 animate-spin" />
                Waiting for a code...
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closePairing}>
              {pairing?.status === 'connected' ? 'Done' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => { if (!open && !deleting) setDeleteTarget(''); }}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl border-line bg-elevated p-6 text-ink shadow-xl sm:rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-ink">Delete WhatsApp connection?</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-ink-3">
              This disconnects <span className="font-medium text-ink-2">{deleteTarget}</span> and removes it from your account. This cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget('')} disabled={deleting}>
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-danger text-white hover:bg-danger/90"
            >
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
